import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { IAward, IGiveawayAuto } from "../../../interfaces";

import { SortOrder } from "antd/es/table/interface";
import { EmitError } from "../../../utils/EmitError";
import { formatCpfCnpj, formatCurrency } from "../../../utils/Masks";
import AwardStatusTag from "../components/AwardStatusTag";
import GiveawaysAutoService from "../../../services/http/services/GiveawaysAutoService";
import { useGetColumnSearchProps } from "../../../hooks/FilterTable/stringClearFiltros";

export interface ITableData extends IAward {
    key: number | undefined;
    app_user_cpfCnpj: string;
    app_user_name: string;
    user_on_app: boolean;
}

export default function useGiveawayAutoWinners() {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [selectedIds, setSelectedIds] = useState<Array<number>>([]);
    const [isConfirmValidateAwardModalVisible, setIsConfirmValidateAwardModalVisible] = useState<boolean>(false);
    const [invalidating, setInvalidating] = useState<boolean>(false);
    const [selectedAward, setSelectedAward] = useState<ITableData | undefined>();
    const refFuncaoLimparFiltrosString = useRef<any>(null);

    const { id } = useParams();
    const navigate = useNavigate();

    const canValidadeOrInvalidateAward = useMemo(() => {
        return !!selectedAward && selectedAward.status === "Em análise";
    }, [selectedAward]);

    const userSelectedHasAccountOnApp = useMemo(() => {

        if (selectedIds.length !== 1 ) {
            return false;
        }

        //busca premio pelo id selecionado
        const selectedAward = tableData.find(award => award.id === selectedIds[0]);

        return selectedAward?.app_user?.id ? true : false;

    }, [selectedIds, tableData]);

    const columns = [
        {
            title: 'Nome',
            dataIndex: 'app_user_name',
            key: 'app_user_name',
            sorter: (a: ITableData, b: ITableData) => a.app_user_name.localeCompare(b.app_user_name),
            showSorterTooltip: false,
            ...useGetColumnSearchProps({dataIndex: 'app_user_name', ref: refFuncaoLimparFiltrosString}),
        },
        {
            title: 'CPF/CNPJ',
            dataIndex: 'app_user_cpfCnpj',
            key: 'app_user_cpfCnpj',
            ...useGetColumnSearchProps({dataIndex: 'app_user_cpfCnpj', ref: refFuncaoLimparFiltrosString, formatter: formatCpfCnpj}),
        },
        {
            title: 'Prêmio',
            dataIndex: 'description',
            key: 'description',
            sorter: (a: ITableData, b: ITableData) => a.description.localeCompare(b.description),
            showSorterTooltip: false,
            ...useGetColumnSearchProps({dataIndex: 'description', ref: refFuncaoLimparFiltrosString}),
            width: '20%',
            ellipsis: true,
        },
        {
            title: 'Valor',
            dataIndex: 'value',
            key: 'value',
            render: (record: number) => `R$ ${formatCurrency(record.toString())}`,
            sorter: (a: ITableData, b: ITableData) => Number(a.value) - Number(b.value),
            showSorterTooltip: false,
        },
        {
            title: 'Bilhete',
            dataIndex: 'winning_ticket_auto_id',
            key: 'winning_ticket_auto_id',
            render: (record: number) => record?.toLocaleString('pt-BR'),
            sorter: (a: ITableData, b: ITableData) => a.winning_ticket_auto_id! - b.winning_ticket_auto_id!,
            showSorterTooltip: false,
        },
        {
            title: 'Ordem',
            dataIndex: 'order',
            key: 'order',
            render: (record: number) => record?.toLocaleString('pt-BR'),
            sorter: (a: ITableData, b: ITableData) => a.order! - b.order!,
            defaultSortOrder: 'ascend' as keyof SortOrder,
            showSorterTooltip: false,
        },
        {
            title: 'Usuário Aplicativo',
            dataIndex: 'user_on_app',
            key: 'user_on_app',
            render: (record: boolean) => (record ? 'Sim' : 'Não'),
            filters: [
                {
                    text: 'Sim',
                    value: true,
                },
                {
                    text: 'Não',
                    value: false,
                },
            ],
            onFilter: (value: any, record: ITableData) => {
                return record.user_on_app === value;
            },
            showSorterTooltip: false,
        },
        {
            title: 'Situação',
            dataIndex: 'status',
            key: 'status',
            sorter: {
                compare: (a: ITableData, b: ITableData) => a.status.localeCompare(b.status)!,
            },
            filters: [
                {
                    text: 'Em análise',
                    value: 'Em análise',
                },
                {
                    text: 'Válido',
                    value: 'Válido',
                },
                {
                    text: 'Inválido',
                    value: 'Inválido',
                },
            ],
            onFilter: (value: any, record: ITableData) => {
                return record.status === value;
            },
            render: (_: any, award: ITableData) => (
                <AwardStatusTag
                    key={award.id}
                    status={award.status}
                    observation={award.observation}
                />
            ),
            showSorterTooltip: false,
        },
    ]

    const setTableDataFromRequest = useCallback((data: IGiveawayAuto) => {
        if (!data.awards) {
            return;
        }

        const newData = data.awards.map((award) => ({
            ...award,
            key: award.id,
            app_user_name: award?.app_user_auto?.name! || award?.app_user?.name!,
            app_user_cpfCnpj: award.app_user_auto?.cpfCnpj! || award.app_user?.cpf!,
            user_on_app: award.app_user?.id ? true : false
        }));
        setTableData(newData);
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function loadWinners() {
            try {
                setIsLoading(true);

                const giveawayData = await GiveawaysAutoService.getGiveawayById({id: Number(id), signal: controller.signal});

                setTableDataFromRequest(giveawayData);
                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'giveawayWinners_aosdpasdpasd');
                setIsLoading(false);
            }
        }

        loadWinners();

        return () => {
            controller.abort();
        }
    }, [setTableDataFromRequest, navigate, id]);

    const onClickTableRow = useCallback((record: any, index: any) => {
        if (selectedIds.includes(record.id)) {
            setSelectedIds((prevState) => prevState.filter((id) => id !== record.id));
        } else {
            setSelectedIds((prevState) => [...prevState, record.id]);
        }
    }, [selectedIds]);

    const onSelectChange = useCallback((newSelectedRowKeys: Array<any>) => {
        if (newSelectedRowKeys.length > selectedIds.length) {
            if (newSelectedRowKeys.length - 1 === selectedIds.length) {
                const newId = newSelectedRowKeys.pop();
                return setSelectedIds((prevState) => [...prevState, newId]);
            }

            if (newSelectedRowKeys.length - 1 > selectedIds.length) {
                return setSelectedIds(newSelectedRowKeys);
            }
        }

        setSelectedIds(newSelectedRowKeys);
    }, [selectedIds]);

    const rowSelection = useMemo(() => ({
        type: 'checkbox' as const,
        selectedRowKeys: selectedIds,
        onChange: onSelectChange,
    }), [selectedIds, onSelectChange]);

    const handleClickViewAppUser = useCallback(() => {
        const reward = tableData.find((r) => r.id === selectedIds[0]);

        if (!reward || !reward?.app_user) {
            return;
        }

        navigate(`/usuarios/aplicativo/visualizar/${reward.app_user?.id}`);
    }, [navigate, selectedIds, tableData]);

    const handleClickGoBack = useCallback(() => {
        window.history.back();
    }, []);

    const handleClickValidateAward = useCallback(async (isValid: boolean) => {
        if (!selectedAward) {
            return;
        }

        if (!isValid) {
            setInvalidating(true);
        }

        setIsConfirmValidateAwardModalVisible(true);
    }, [selectedAward]);

    const handleValidateFinished = useCallback((award: IAward) => {
        setTableData((prevState) => {
            const awardIndex = prevState.findIndex((a) => a.id === award.id);

            if (awardIndex < 0) {
                return prevState;
            }

            const newTableData = [ ...prevState ];

            newTableData[awardIndex] = {
                ...newTableData[awardIndex],
                ...award,
            }

            return newTableData;
        });

        setSelectedIds([]);
        setIsConfirmValidateAwardModalVisible(false);
        setSelectedAward(undefined);
        setInvalidating(false);
    }, []);

    const handleCancelValidateAward = useCallback(() => {
        setIsConfirmValidateAwardModalVisible(false);
        setInvalidating(false);
    }, []);

    useEffect(() => {
        if (selectedIds.length !== 1) {
            setSelectedAward(undefined);
            return;
        }

        const selectedAwardData = tableData.find((award) => award.id === selectedIds[0]);

        setSelectedAward(selectedAwardData);
    }, [selectedIds, tableData]);

    return {
        isLoading,
        columns,
        onClickTableRow,
        rowSelection,
        selectedIds,
        tableData,
        handleClickViewAppUser,
        handleClickGoBack,
        handleClickValidateAward,
        isConfirmValidateAwardModalVisible,
        invalidating,
        handleValidateFinished,
        handleCancelValidateAward,
        canValidadeOrInvalidateAward,
        selectedAward,
        userSelectedHasAccountOnApp
    }
}
// TODO: Voltar nesse arquivo
